'use strict';

// For testing purposes, you can set this to false to disable the sanitization of Mongo keys.
const SANITIZE_MONGO_KEYS_ENABLED = true;

// MongoDB (BSON / LoopBack 2) no permite '.' en nombres de campos
// ni keys que empiezan con '$' (ej. $$hashKey de Angular).
function toMongoSafeKey(key) {
    if (!SANITIZE_MONGO_KEYS_ENABLED) {
        return String(key);
    }
    return String(key).replace(/\./g, '_');
}

function sanitizeMongoKeys(value) {
    if (!SANITIZE_MONGO_KEYS_ENABLED) {
        return value;
    }
    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
            sanitizeMongoKeys(value[i]);
        }
        return value;
    }
    if (value && typeof value === 'object') {
        const originalKeys = Object.keys(value);
        for (let i = 0; i < originalKeys.length; i++) {
            sanitizeMongoKeys(value[originalKeys[i]]);
        }

        // Renombrar en el mismo lugar. Si se hace value[nueva] = value[vieja]
        // y después delete, la clave nueva queda al final del objeto.
        const rebuilt = {};
        let changed = false;
        for (let i = 0; i < originalKeys.length; i++) {
            const key = originalKeys[i];
            if (key.charAt(0) === '$') {
                changed = true;
                continue;
            }
            const newKey = key.indexOf('.') === -1 ? key : toMongoSafeKey(key);
            if (newKey !== key) {
                changed = true;
                if (Object.prototype.hasOwnProperty.call(rebuilt, newKey) ||
                    Object.prototype.hasOwnProperty.call(value, newKey)) {
                    continue;
                }
            } else if (Object.prototype.hasOwnProperty.call(rebuilt, newKey)) {
                continue;
            }
            rebuilt[newKey] = value[key];
        }

        if (!changed) {
            return value;
        }

        for (let i = 0; i < originalKeys.length; i++) {
            delete value[originalKeys[i]];
        }
        const nextKeys = Object.keys(rebuilt);
        for (let i = 0; i < nextKeys.length; i++) {
            value[nextKeys[i]] = rebuilt[nextKeys[i]];
        }
    }
    return value;
}

function collectDottedKeys(value, acc) {
    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
            collectDottedKeys(value[i], acc);
        }
        return;
    }
    if (!value || typeof value !== 'object') {
        return;
    }
    Object.keys(value).forEach(function (key) {
        if (key.indexOf('.') !== -1) {
            acc.push(key);
        }
        collectDottedKeys(value[key], acc);
    });
}

// Reemplaza claves completas (las más largas primero) sin tocar otros puntos del texto.
function replaceExactKeys(text, keys) {
    if (typeof text !== 'string' || !keys.length) {
        return text;
    }
    const unique = Array.from(new Set(keys)).sort(function (a, b) {
        return b.length - a.length;
    });
    let out = text;
    unique.forEach(function (key) {
        const safe = toMongoSafeKey(key);
        if (safe !== key) {
            out = out.split(key).join(safe);
        }
    });
    return out;
}

// MongoDB no persiste claves con '.'. En integraciones esas claves viven en
// populateValues y se repiten como texto en afterHook / returnData.fields[].name.
function prepareIntegration(integration) {
    if (!SANITIZE_MONGO_KEYS_ENABLED || !integration || typeof integration !== 'object') {
        return integration;
    }
    const dottedKeys = [];
    collectDottedKeys(integration, dottedKeys);
    sanitizeMongoKeys(integration);
    const configuration = integration.configuration;
    if (!configuration || typeof configuration !== 'object') {
        return integration;
    }
    if (typeof configuration.afterHook === 'string') {
        configuration.afterHook = replaceExactKeys(configuration.afterHook, dottedKeys);
    }
    const fields = configuration.returnData && configuration.returnData.fields;
    if (Array.isArray(fields)) {
        fields.forEach(function (field) {
            if (field && typeof field.name === 'string') {
                field.name = replaceExactKeys(field.name, dottedKeys);
            }
        });
    }
    return integration;
}

sanitizeMongoKeys.toMongoSafeKey = toMongoSafeKey;
sanitizeMongoKeys.prepareIntegration = prepareIntegration;
module.exports = sanitizeMongoKeys;
